import React from 'react';
import Icon from '../../../components/AppIcon';

const TransactionDetails = ({ transaction }) => {
  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    })?.format(amount);
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRiskScoreColor = (score) => {
    if (score <= 30) return 'text-success bg-success/10';
    if (score <= 60) return 'text-warning bg-warning/10';
    return 'text-error bg-error/10';
  };

  const getRiskLevel = (score) => {
    if (score <= 30) return 'Low Risk';
    if (score <= 60) return 'Medium Risk';
    return 'High Risk';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Transaction Details</h2>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">ID:</span>
          <span className="font-mono text-sm text-foreground">{transaction?.id}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sender Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground flex items-center">
            <Icon name="User" size={20} className="mr-2" />
            Sender Information
          </h3>
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Name:</span>
              <span className="text-sm font-medium text-foreground">{transaction?.sender?.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Email:</span>
              <span className="text-sm text-foreground">{transaction?.sender?.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Phone:</span>
              <span className="text-sm text-foreground">{transaction?.sender?.phone}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Country:</span>
              <span className="text-sm text-foreground">{transaction?.sender?.country}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">KYC Status:</span>
              <span className={`text-sm px-2 py-1 rounded-full ${
                transaction?.sender?.kycStatus === 'verified' 
                  ? 'bg-success/10 text-success' :'bg-warning/10 text-warning'
              }`}>
                {transaction?.sender?.kycStatus}
              </span>
            </div>
          </div>
        </div>

        {/* Recipient Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground flex items-center">
            <Icon name="UserCheck" size={20} className="mr-2" />
            Recipient Information
          </h3>
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Name:</span>
              <span className="text-sm font-medium text-foreground">{transaction?.recipient?.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Bank:</span>
              <span className="text-sm text-foreground">{transaction?.recipient?.bank}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">IBAN:</span>
              <span className="text-sm font-mono text-foreground">{transaction?.recipient?.iban}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">SWIFT/BIC:</span>
              <span className="text-sm font-mono text-foreground">{transaction?.recipient?.swift}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Country:</span>
              <span className="text-sm text-foreground">{transaction?.recipient?.country}</span>
            </div>
          </div>
        </div>

        {/* Transfer Details */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground flex items-center">
            <Icon name="ArrowRightLeft" size={20} className="mr-2" />
            Transfer Details
          </h3>
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Send Amount:</span>
              <span className="text-sm font-medium text-foreground">
                {formatCurrency(transaction?.sendAmount, transaction?.sendCurrency)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Exchange Rate:</span>
              <span className="text-sm text-foreground">{transaction?.exchangeRate}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Fees:</span>
              <span className="text-sm text-foreground">
                {formatCurrency(transaction?.fees, transaction?.sendCurrency)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Receive Amount:</span>
              <span className="text-sm font-medium text-foreground">
                {formatCurrency(transaction?.receiveAmount, transaction?.receiveCurrency)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Transfer Type:</span>
              <span className="text-sm text-foreground capitalize">{transaction?.transferType}</span>
            </div>
          </div>
        </div>

        {/* Risk Assessment */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground flex items-center">
            <Icon name="Shield" size={20} className="mr-2" />
            Risk Assessment
          </h3>
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Risk Score:</span>
              <div className="flex items-center space-x-2">
                <span className={`text-sm px-2 py-1 rounded-full font-medium ${getRiskScoreColor(transaction?.riskScore)}`}>
                  {transaction?.riskScore}/100
                </span>
                <span className="text-sm text-foreground">{getRiskLevel(transaction?.riskScore)}</span>
              </div>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Source of Funds:</span>
              <span className="text-sm text-foreground">{transaction?.sourceOfFunds}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Transfer Purpose:</span>
              <span className="text-sm text-foreground">{transaction?.transferPurpose}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Submitted:</span>
              <span className="text-sm text-foreground">{formatDate(transaction?.submittedAt)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetails;